import { _lcs } from "../i18n";

/**
 * Is the API reachable — and does the guest know when it is not?
 *
 * `navigator.onLine` only says whether there is a network interface; a hotel
 * Wi-Fi behind a login page, or a phone in a valley with one bar, is "online"
 * and still gets nothing through. So the browser's online/offline events, a
 * failed API call (core/Api) and the return to the foreground all only
 * trigger a check: a real request to the API with a short timeout. Any HTTP
 * answer — a 404 included — proves the way there works; only a network error
 * or the timeout counts as offline.
 *
 * Offline, a quiet banner says what the guest is looking at (the cached copy,
 * see sw.js); the app checks again every RETRY_MS and on every chance above.
 * Back online, the banner goes, and an "app-online" event lets parts of the
 * app catch up (chat dot, usage beacons).
 */
export class Connectivity
{
    static PROBE_TIMEOUT_MS = 5000;
    static RETRY_MS = 15000;

    constructor( app )
    {
        this.app = app;
        this.online = true;
        this._banner = null;
        this._retry = null;
        this._probing = null;
        this._started = false;
    }

    start()
    {
        if ( this._started )
        {
            return;
        }
        this._started = true;

        window.addEventListener( "online", () => this.check() );
        window.addEventListener( "offline", () => this.check() );

        // Opened without a network: say so straight away rather than after
        // the first request has timed out.
        if ( navigator.onLine === false )
        {
            this.check();
        }
    }

    /** Probe now (concurrent callers share one probe). Resolves to the new state. */
    check()
    {
        if ( !this._probing )
        {
            this._probing = this._probe().then( ( online ) =>
            {
                this._probing = null;
                this._set( online );
                return online;
            } );
        }
        return this._probing;
    }

    /** A request failed without an HTTP answer — find out whether that was the network. */
    reportFailure()
    {
        this.check();
    }

    async _probe()
    {
        const controller = typeof AbortController === "function" ? new AbortController() : null;
        const timer = setTimeout( () => controller && controller.abort(), Connectivity.PROBE_TIMEOUT_MS );
        try
        {
            // The API root answers 404 — fast, uncached (not a content route for
            // the Service Worker) and proof that the API is reachable. no-cors:
            // an opaque answer is answer enough, and a missing CORS header must
            // not read as "offline".
            await fetch( this.app.config.get( "api" ) + "/", {
                method : "GET",
                mode : "no-cors",
                cache : "no-store",
                signal : controller ? controller.signal : undefined
            } );
            return true;
        }
        catch ( e )
        {
            return false;
        }
        finally
        {
            clearTimeout( timer );
        }
    }

    _set( online )
    {
        if ( online === this.online )
        {
            return;
        }
        this.online = online;
        document.documentElement.classList.toggle( "is-offline", !online );

        if ( online )
        {
            clearInterval( this._retry );
            this._retry = null;
            if ( this._banner )
            {
                this._banner.remove();
                this._banner = null;
            }
            this.app.dispatchEvent( new CustomEvent( "app-online" ) );
            return;
        }

        this._banner = this.app.view.banner( {
            icon : "wifi-off",
            text : _lcs( "Keine Verbindung — du siehst die zuletzt geladenen Inhalte." ),
            tone : "quiet",
            dismissible : false
        } );
        this._retry = setInterval( () => this.check(), Connectivity.RETRY_MS );
        this.app.dispatchEvent( new CustomEvent( "app-offline" ) );
    }
}
